import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { Button } from "@design-system/Button/Button";
import { useCallback, useMemo, useState } from "react";
import { CommandIsland } from "@design-system/CommandIsland/CommandIsland";
import classNames from "classnames";
import { CheckIcon, ClockIcon, TriangleSmallIcon } from "@icons";
import { Picker } from "@design-system/Picker/Picker.lib";
import type { PickerItemData } from "@design-system/Picker/Picker.types";
import {
    EntityAvatar,
    type EntityAvatarEntity,
} from "@components/avatar/EntityAvatar";
import { BreakableText } from "@design-system/BreakableText/BreakableText";
import {
    BreadcrumbItem,
    Breadcrumbs,
} from "@design-system/Breadcrumbs/Breadcrumbs";
import { ApplyCloseTemplateEntityRow } from "./applyCloseTemplate.schema";
import { ApplyCloseTemplateEntityPreview } from "./ApplyCloseTemplateEntityPreview";
import styles from "./ApplyCloseTemplateDrawer.module.scss";
import previewStyles from "./ApplyCloseTemplatePreviewStep.module.scss";
import { Tooltip } from "@design-system/Tooltip/Tooltip";

export interface SelectedEntityEntry {
    entityIdStr: string;
    row: ApplyCloseTemplateEntityRow;
}

function toEntityAvatarEntity(
    row: ApplyCloseTemplateEntityRow,
): EntityAvatarEntity {
    return {
        name: row.entityName,
        iconColor: row.entityIconColor,
        logo: row.entityLogo ?? null,
    };
}

interface Props {
    closeTemplateId?: string;
    entries: SelectedEntityEntry[];
    appliedEntityIds: ReadonlySet<number>;
    applyingEntityId: number | null;
    isApplyingBulk: boolean;
    onBack: () => void;
    onApplyAll: () => void;
    onClose: () => void;
    /** Defaults to "Apply". */
    applyButtonLabel?: string;
    /** Defaults to "Step 4 of 4 · Preview selection". */
    subtitle?: string;
}

interface EntityRowProps {
    entityIdStr: string;
    row: ApplyCloseTemplateEntityRow;
    isSelected: boolean;
    isApplied: boolean;
    isApplying: boolean;
    onSelect: (entityIdStr: string) => void;
}

const EntityRow = ({
    entityIdStr,
    row,
    isSelected,
    isApplied,
    isApplying,
    onSelect,
}: EntityRowProps) => (
    <div
        onClick={() => onSelect(entityIdStr)}
        className={classNames(previewStyles.entityRow, {
            [previewStyles.selected]: isSelected,
        })}
    >
        <div className={previewStyles.entityHeader}>
            <EntityAvatar
                size="xs"
                entity={toEntityAvatarEntity(row)}
                aria-hidden
            />
            <BreakableText
                className={previewStyles.entityName}
                text={row.entityName}
                showTooltip
            />
            {isApplied && (
                <span
                    className={classNames(
                        previewStyles.statusIcon,
                        previewStyles.statusApplied,
                    )}
                    aria-label="Applied"
                    title="Applied"
                >
                    <CheckIcon />
                </span>
            )}
            {!isApplied && isApplying && (
                <span
                    className={classNames(
                        previewStyles.statusIcon,
                        previewStyles.statusApplying,
                    )}
                    aria-label="Applying"
                    title="Applying"
                >
                    <ClockIcon />
                </span>
            )}
        </div>
    </div>
);

type ViewingAs = "organization" | "client";

const VIEWING_AS_OPTIONS: Array<PickerItemData<ViewingAs>> = [
    { value: "organization", label: "Viewing as Organization" },
    { value: "client", label: "Viewing as Client" },
];

export const ApplyCloseTemplateStepReview = ({
    closeTemplateId,
    entries,
    appliedEntityIds,
    applyingEntityId,
    isApplyingBulk,
    onBack,
    onApplyAll,
    onClose,
    applyButtonLabel = "Apply",
    subtitle = "Step 4 of 4 · Preview selection",
}: Props) => {
    const [selectedEntityId, setSelectedEntityId] = useState<string | null>(
        entries[0]?.entityIdStr ?? null,
    );
    const [viewingAs, setViewingAs] = useState<ViewingAs>("organization");
    const selectedEntity = useMemo(() => {
        if (entries.length === 0) {
            return null;
        }

        if (!selectedEntityId) {
            return entries[0];
        }

        return (
            entries.find((entry) => entry.entityIdStr === selectedEntityId) ??
            entries[0]
        );
    }, [entries, selectedEntityId]);

    const handleSelectEntity = useCallback((entityIdStr: string) => {
        setSelectedEntityId(entityIdStr);
    }, []);

    const handleOpenClientWorkspace = useCallback(() => {
        if (!selectedEntity) {
            return;
        }
        const url = new URL("/open", window.location.origin);
        url.searchParams.set("workspaceId", selectedEntity.row.workspaceId);
        url.searchParams.set("target", "work");
        url.searchParams.set("entityId", selectedEntity.entityIdStr);
        window.open(url.toString(), "_blank", "noopener,noreferrer");
    }, [selectedEntity]);

    const currentIndex = useMemo(() => {
        if (!selectedEntity) {
            return -1;
        }
        return entries.findIndex(
            (e) => e.entityIdStr === selectedEntity.entityIdStr,
        );
    }, [entries, selectedEntity]);

    const canGoToPrevious = currentIndex > 0;
    const canGoToNext = currentIndex < entries.length - 1;

    const goToPrevious = useCallback(() => {
        if (currentIndex > 0) {
            setSelectedEntityId(entries[currentIndex - 1].entityIdStr);
        }
    }, [currentIndex, entries]);

    const goToNext = useCallback(() => {
        if (currentIndex < entries.length - 1) {
            setSelectedEntityId(entries[currentIndex + 1].entityIdStr);
        }
    }, [currentIndex, entries]);
    const groupedEntries = useMemo(
        () =>
            entries.reduce<Record<string, SelectedEntityEntry[]>>(
                (acc, entry) => {
                    const clientLabel = entry.row.clientLabel;
                    if (!acc[clientLabel]) {
                        acc[clientLabel] = [];
                    }
                    acc[clientLabel].push(entry);
                    return acc;
                },
                {} as Record<string, SelectedEntityEntry[]>,
            ),
        [entries],
    );

    if (entries.length === 0) {
        return <p className="text-muted small">No entities selected.</p>;
    }

    const pendingCount = entries.filter(
        (e) => !appliedEntityIds.has(Number(e.entityIdStr)),
    ).length;
    const allApplied =
        entries.length > 0 &&
        entries.every((e) => appliedEntityIds.has(Number(e.entityIdStr)));

    return (
        <>
            <div className={styles.subtitleContainer}>{subtitle}</div>
            <div className={previewStyles.container}>
                <div className={previewStyles.listContainer}>
                    <div className={previewStyles.headerRow}>
                        Selected entities
                    </div>
                    <div>
                        {Object.entries(groupedEntries).map(
                            ([clientLabel, clientEntries]) => (
                                <div key={clientLabel}>
                                    <div className={previewStyles.clientRow}>
                                        {clientLabel}
                                    </div>
                                    {clientEntries.map(
                                        ({ entityIdStr, row }) => {
                                            const entityIdNum =
                                                Number(entityIdStr);
                                            return (
                                                <EntityRow
                                                    key={entityIdStr}
                                                    entityIdStr={entityIdStr}
                                                    row={row}
                                                    isSelected={
                                                        selectedEntity?.entityIdStr ===
                                                        entityIdStr
                                                    }
                                                    isApplied={appliedEntityIds.has(
                                                        entityIdNum,
                                                    )}
                                                    isApplying={
                                                        applyingEntityId ===
                                                        entityIdNum
                                                    }
                                                    onSelect={
                                                        handleSelectEntity
                                                    }
                                                />
                                            );
                                        },
                                    )}
                                </div>
                            ),
                        )}
                    </div>
                </div>
                <div className={previewStyles.previewContainer}>
                    <div className={previewStyles.previewHeaderRow}>
                        <div className={previewStyles.previewHeaderLeft}>
                            {selectedEntity && (
                                <Breadcrumbs>
                                    <BreadcrumbItem
                                        className={previewStyles.clientLevel}
                                    >
                                        {selectedEntity.row.clientLabel}
                                    </BreadcrumbItem>
                                    <BreadcrumbItem
                                        current
                                        leftIcon={
                                            <EntityAvatar
                                                size="xs"
                                                entity={toEntityAvatarEntity(
                                                    selectedEntity.row,
                                                )}
                                                aria-hidden
                                            />
                                        }
                                    >
                                        {selectedEntity.row.entityName}
                                    </BreadcrumbItem>
                                </Breadcrumbs>
                            )}
                        </div>
                        <div className={previewStyles.previewHeaderRight}>
                            {entries.length > 1 && (
                                <>
                                    <div
                                        className={previewStyles.indexContainer}
                                    >
                                        <span
                                            className={
                                                previewStyles.currentIndex
                                            }
                                        >
                                            {currentIndex + 1}
                                        </span>
                                        <span
                                            className={previewStyles.totalTasks}
                                        >
                                            {" "}
                                            / {entries.length}
                                        </span>
                                    </div>
                                    <Tooltip content="Navigate down">
                                        <Button
                                            variant="secondary"
                                            size="md"
                                            iconOnly
                                            onClick={goToNext}
                                            disabled={!canGoToNext}
                                            aria-label="Navigate down"
                                        >
                                            <TriangleSmallIcon />
                                        </Button>
                                    </Tooltip>
                                    <Tooltip content="Navigate up">
                                        <Button
                                            variant="secondary"
                                            size="md"
                                            iconOnly
                                            onClick={goToPrevious}
                                            disabled={!canGoToPrevious}
                                            aria-label="Navigate up"
                                        >
                                            <TriangleSmallIcon className="icon-rotate-180" />
                                        </Button>
                                    </Tooltip>
                                    <div
                                        className={
                                            previewStyles.navigationDivider
                                        }
                                    />
                                </>
                            )}
                            <Button
                                variant="primary"
                                size="md"
                                onClick={handleOpenClientWorkspace}
                                disabled={!selectedEntity}
                            >
                                Open client
                            </Button>
                            <Picker.Select
                                items={VIEWING_AS_OPTIONS}
                                value={viewingAs}
                                onValueChange={(value) =>
                                    setViewingAs(value ?? "organization")
                                }
                                trigger={
                                    <Picker.Button
                                        variant="primary"
                                        size="md"
                                        className="ml-1"
                                    />
                                }
                            />
                        </div>
                    </div>
                    <div className={previewStyles.previewContentContainer}>
                        <div className={previewStyles.previewContent}>
                            {selectedEntity && closeTemplateId ? (
                                <ApplyCloseTemplateEntityPreview
                                    closeTemplateId={closeTemplateId}
                                    selectedEntity={selectedEntity}
                                    viewingAs={viewingAs}
                                />
                            ) : (
                                <div>No entity selected</div>
                            )}
                        </div>
                        <CommandIsland alignEnd>
                            <ButtonWithLoader
                                variant="secondary"
                                size="md"
                                type="button"
                                loading={false}
                                onClick={onBack}
                            >
                                Back
                            </ButtonWithLoader>
                            {allApplied ? (
                                <ButtonWithLoader
                                    variant="primary"
                                    size="md"
                                    type="button"
                                    loading={false}
                                    onClick={onClose}
                                >
                                    Finish
                                </ButtonWithLoader>
                            ) : (
                                <ButtonWithLoader
                                    variant="primary"
                                    size="md"
                                    type="button"
                                    loading={isApplyingBulk}
                                    disabled={
                                        pendingCount === 0 ||
                                        applyingEntityId != null ||
                                        isApplyingBulk
                                    }
                                    hotKey="mod+enter"
                                    hotKeyOptions={{
                                        description: applyButtonLabel,
                                        preventDefault: true,
                                    }}
                                    hotKeyDisplayPosition="inline"
                                    onClick={onApplyAll}
                                >
                                    {applyButtonLabel}
                                </ButtonWithLoader>
                            )}
                        </CommandIsland>
                    </div>
                </div>
            </div>
        </>
    );
};
